<template>
  <div class="ft-root">
    <!-- Empty state -->
    <div v-if="fieldGroups.length === 0 && independentFields.length === 0" class="ft-empty">
      <i class="fa fa-list-ul"></i>
      <p>No fields detected</p>
    </div>

    <!-- Groups -->
    <div v-if="fieldGroups.length > 0" class="ft-section">
      <div class="ft-section-label">Groups</div>
      <div
        v-for="group in fieldGroups"
        :key="group.prefix"
        class="ft-group"
      >
        <div class="ft-group-header">
          <span class="ft-group-name">{{ group.name }}</span>
          <button
            v-if="canEdit"
            class="ft-group-edit"
            @click="$emit('edit-group', group.prefix)"
            title="Edit group"
          >
            <i class="fa fa-cog"></i>
          </button>
        </div>
        <div class="ft-group-fields">
          <div
            v-for="field in group.fields"
            :key="field.value"
            class="ft-field"
            :class="{ 'is-populated': isFieldPopulated(field.value) }"
            :id="`tooltip-field-${field.value}`"
            @click="(e) => $emit('field-click', { event: e, field })"
          >
            <div class="ft-field-indicator"></div>
            <div class="ft-field-info">
              <span class="ft-field-name">{{ field.name }}</span>
              <span class="ft-field-value" v-if="getFieldValue(field.value)">{{ getFieldValue(field.value) }}</span>
            </div>
            <div class="ft-field-options" v-if="getFieldValueOptions(field)" @click.stop>
              <select
                :value="getFieldValue(field.value)"
                @change="$emit('update-field-value', { field, value: $event.target.value })"
                class="ft-field-select"
              >
                <option
                  v-for="opt in getFieldValueOptions(field)"
                  :key="opt"
                  :value="opt"
                >{{ opt }}</option>
              </select>
            </div>
            <i class="fa fa-chevron-right ft-field-arrow"></i>
          </div>
        </div>
      </div>
    </div>

    <!-- Independent fields -->
    <div v-if="independentFields.length > 0" class="ft-section">
      <div class="ft-section-label">Fields</div>
      <div class="ft-fields-list">
        <div
          v-for="field in independentFields"
          :key="field.value"
          class="ft-field"
          :class="{ 'is-populated': isFieldPopulated(field.value) }"
          :id="`tooltip-field-${field.value}`"
          @click="(e) => $emit('field-click', { event: e, field })"
        >
          <div class="ft-field-indicator"></div>
          <div class="ft-field-info">
            <span class="ft-field-name">{{ field.name }}</span>
            <span class="ft-field-value" v-if="getFieldValue(field.value)">{{ getFieldValue(field.value) }}</span>
          </div>
          <i class="fa fa-chevron-right ft-field-arrow"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "FieldsTab",
  props: {
    fieldGroups: {
      type: Array,
      required: true,
    },
    independentFields: {
      type: Array,
      required: true,
    },
    tabPosition: {
      type: String,
      required: true,
    },
    canEdit: {
      type: Boolean,
      default: false,
    },
    getFieldValue: {
      type: Function,
      required: true,
    },
    getFieldValueOptions: {
      type: Function,
      required: true,
    },
    isFieldPopulated: {
      type: Function,
      required: true,
    },
  },
};
</script>

<style scoped lang="scss">
$bg-base:        #0d0f17;
$bg-surface:     #13161f;
$bg-elevated:    #1a1d29;
$bg-hover:       #1f2333;
$border:         #2d3250;
$border-sub:     #232840;
$accent:         #5b7cf6;
$accent-dim:     rgba(#5b7cf6, 0.12);
$text-primary:   #e8eaf2;
$text-secondary: #7c87a8;
$text-muted:     #4a5270;
$success:        #4ecb8d;
$success-dim:    rgba(#4ecb8d, 0.12);
$warning:        #f6b955;
$warning-dim:    rgba(#f6b955, 0.12);
$radius:         6px;
$transition:     all 0.18s ease;

.ft-root {
  padding: 4px 0;
}

.ft-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 16px;
  gap: 8px;
  color: $text-muted;
  text-align: center;

  i { font-size: 28px; opacity: 0.5; }
  p { font-size: 13px; margin: 0; }
}

.ft-section {
  & + & { margin-top: 16px; }
}

.ft-section-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: $text-muted;
  padding: 0 4px;
  margin-bottom: 8px;
}

// ─── Group card ──────────────────────────────────────────────────────────────
.ft-group {
  background: $bg-elevated;
  border: 1px solid $border-sub;
  border-radius: $radius;
  transition: border-color 0.15s;
  margin-bottom: 8px;

  &:hover { border-color: $border; }
  &:last-child { margin-bottom: 0; }
}

.ft-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: $bg-surface;
  border-radius: $radius $radius 0 0;
  border-bottom: 1px solid $border-sub;
}

.ft-group-name {
  flex: 1;
  font-size: 12px;
  font-weight: 600;
  color: $text-primary;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ft-group-edit {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;

  &:hover { background: $bg-hover; color: $text-secondary; }
}

.ft-group-fields {
  padding: 4px;
}

// ─── Field row ───────────────────────────────────────────────────────────────
.ft-fields-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ft-field {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: $transition;
  position: relative;

  &:hover {
    background: $bg-hover;
    .ft-field-arrow { opacity: 1; }
  }

  &.is-populated {
    .ft-field-indicator { background: $success; box-shadow: 0 0 6px rgba($success, 0.3); }
    .ft-field-name { color: $text-primary; }
  }
}

.ft-field-indicator {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: $warning;
  box-shadow: 0 0 6px rgba($warning, 0.3);
  flex-shrink: 0;
  transition: $transition;
}

.ft-field-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ft-field-name {
  font-size: 12px;
  font-weight: 500;
  color: $text-secondary;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 0.15s;
  font-family: 'SFMono-Regular', 'Consolas', monospace;
}

.ft-field-value {
  font-size: 11px;
  color: $text-muted;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ft-field-arrow {
  font-size: 9px;
  color: $text-muted;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
}

.ft-field-options {
  flex-shrink: 0;
}

.ft-field-select {
  height: 24px;
  padding: 0 6px;
  padding-right: 20px;
  background: $bg-elevated;
  border: 1px solid $border;
  border-radius: 4px;
  color: $text-secondary;
  font-size: 11px;
  cursor: pointer;
  outline: none;
  transition: $transition;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='8' height='5' fill='none'%3E%3Cpath d='M1 1l3 3 3-3' stroke='%237c87a8' stroke-width='1.2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 5px center;
  max-width: 90px;

  &:hover { border-color: lighten($border, 8%); color: $text-primary; }
  &:focus { border-color: $accent; }
}
</style>
